// React bindings for @govard/feature-flags.
// Usage:
//   <FlagsProvider registry={registry} ctx={{ workspaceId, userId }}>
//     {children}
//   </FlagsProvider>
//   const proMode = useFeatureFlag('pro-mode');

import { createContext, createElement, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { FlagId, FlagResolutionContext, FlagsRegistry } from '../types.js';

export interface FlagsContextValue {
  registry: FlagsRegistry;
  ctx: FlagResolutionContext;
}

const FlagsContext = createContext<FlagsContextValue | null>(null);
FlagsContext.displayName = 'GovardFlagsContext';

export interface FlagsProviderProps {
  registry: FlagsRegistry;
  ctx: FlagResolutionContext;
  children?: ReactNode;
}

export function FlagsProvider(props: FlagsProviderProps): ReactNode {
  const value = useMemo<FlagsContextValue>(
    () => ({ registry: props.registry, ctx: props.ctx }),
    [props.registry, props.ctx],
  );
  return createElement(FlagsContext.Provider, { value }, props.children);
}

/**
 * Reads a flag from the surrounding <FlagsProvider>. Throws if the provider
 * is missing — that signals a wiring bug and should fail fast in dev.
 */
export function useFeatureFlag(flagId: FlagId): boolean {
  const v = useContext(FlagsContext);
  if (!v) {
    throw new Error('[@govard/feature-flags] useFeatureFlag must be used within <FlagsProvider>.');
  }
  return v.registry.isEnabled(flagId, v.ctx);
}

export { FlagsContext };
